// frontend/modules/customer-category/views/CustomerCategoryEditView.tsx
import React, { useEffect, useRef } from 'react';
import { Card, Typography, Button, Spin, Alert } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@/components/common/ThemeProvider';
import CustomerCategoryPageForm from '../components/CustomerCategoryPageForm';
import { CustomerCategoryDTO } from '@/shared/api/model';
import { useCustomerCategories } from '../hooks/useCustomerCategories';

const { Title } = Typography;

const CustomerCategoryEditView: React.FC = () => {
    const { dark } = useTheme();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const {
        activeCategory,
        isLoading,
        updateCategory,
        fetchCategoryById
    } = useCustomerCategories();
    
    // Debug: Track renders and state changes
    const renderCount = React.useRef(0);
    renderCount.current += 1;
    console.log(`[CustomerCategoryEditView] Render #${renderCount.current}`, {
        id,
        isLoading,
        activeCategoryId: activeCategory?.id,
        hasActiveCategory: !!activeCategory
    });

    // Use ref to store the fetch function to avoid recreating useEffect
    const fetchCategoryByIdRef = useRef(fetchCategoryById);
    fetchCategoryByIdRef.current = fetchCategoryById;

    useEffect(() => {
        // Only fetch if we have an ID and either:
        // 1. No active category loaded, OR
        // 2. The loaded category ID doesn't match the requested ID
        // 3. And we're not currently loading
        if (id && !isLoading && (!activeCategory || activeCategory.id !== parseInt(id))) {
            fetchCategoryByIdRef.current(parseInt(id));
        }
    }, [id]); // Only depend on id, not activeCategory?.id to prevent loop

    const handleSubmit = async (formData: CustomerCategoryDTO) => {
        try {
            await updateCategory(formData);
            navigate('/customer-categories');
        } catch (error) {
            console.error('Error updating customer category:', error);
            throw error;
        }
    };

    const handleCancel = () => {
        navigate('/customer-categories');
    };

    // Show initial loading only if we don't have any category data yet
    const showInitialLoading = isLoading && !activeCategory;
    
    if (showInitialLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spin size="large" />
            </div>
        );
    }

    // If we don't have activeCategory and not loading, something went wrong
    if (!activeCategory) {
        return (
            <div className="flex justify-center items-center h-64">
                <Alert
                    message="Category Not Found"
                    description="The requested category could not be loaded."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={handleCancel}
                        type="text"
                    >
                        Back to Categories
                    </Button>
                    <Title level={2} className={`mb-0 ${dark ? 'text-white' : ''}`}>
                        Edit Customer Category: {activeCategory.name}
                    </Title>
                </div>
            </div>

            <Card className={dark ? 'bg-gray-800' : ''}>
                <Spin spinning={isLoading}>
                    <CustomerCategoryPageForm
                        mode="edit"
                        initialData={activeCategory}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                    />
                </Spin>
            </Card>
        </div>
    );
};

// Enable why-did-you-render tracking for this component
// CustomerCategoryEditView.whyDidYouRender = true;

export default CustomerCategoryEditView;